import React, { useCallback, useEffect, useState } from 'react';

import axios, { AxiosError } from 'axios';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { createContainer, useContainer } from 'unstated-next';

import AdminAuthContainer from './AdminAuthContainer';
import { UserFromCSV, LoadingStatus } from './components/AdminUsers/types';
import { upsertUsersOptimistic } from './components/AdminUsers/utils/upsertUsersOptimistic';
import { UserRole } from './constants/user-roles';
import { USERS, USERS_DATA_PORTAL } from '../../config/apiPaths';
import { useSchoolInfo } from '../../hooks/useSchoolInfo';
import {
    CreateUserRequestInput,
    CreateUsersRequestInput,
    UpdateUserRequestInput,
    UsersRecord,
    UsersRecordExtended
} from '../../types/apiTypes';
import { getUserUuidHeaders } from '../../utils/httpUtils';
import { getUrls } from '../../utils/url';

export const OPTIMISTIC_NEW_USER_ID = -1;

type UsersErrorResponse = {
    error: string;
};

export type UpdateUserInput = Required<UsersRecord> & { password?: string };

const useAdminUsers = () => {
    const { t } = useTranslation('portal');
    const [usersFetchStatus, setUsersFetchStatus] = useState<LoadingStatus>('INITIAL');
    const [users, setUsers] = useState<UsersRecordExtended[]>([]);
    const [usersFetchError, setUsersFetchError] = useState<AxiosError<UsersErrorResponse> | undefined>(undefined);
    const { uuid, authMode, showDemoContent } = useContainer(AdminAuthContainer);

    const [selectedRoles, setSelectedRoles] = useState<UserRole[]>([]);
    const [usersForBulkUpload, setUsersForBulkUpload] = useState<UserFromCSV[]>([]); // Warning: may make pages slower if 5000 users are kept in state.
    const [errorIndexes, setErrorIndexes] = useState<number[]>([]);
    const [maxUsersLimitExceeded, setMaxUsersLimitExceeded] = useState<boolean>(false);
    const [errorsAcknowledged, setErrorsAcknowledged] = useState(false);
    const [duplicateIndexes, setDuplicateIndexes] = useState<number[]>([]);
    const [bulkUpsertUsersStatus, setBulkUpsertUsersStatus] = useState<LoadingStatus>('INITIAL');
    const [fileNameForCSV, setFileNameForCSV] = useState('');

    React.useEffect(() => {
        console.log('authMode changed', authMode);
        setUsersFetchStatus('INITIAL');
    }, [authMode]);

    React.useEffect(() => {
        console.log('showDemoContent changed', showDemoContent);
        setUsersFetchStatus('INITIAL');
    }, [showDemoContent]);

    const { enqueueSnackbar } = useSnackbar();

    const { api } = getUrls();

    useSchoolInfo();

    const resetBulkUploadState = () => {
        setFileNameForCSV('');
        setUsersForBulkUpload([]);
        setBulkUpsertUsersStatus('INITIAL');
        setSelectedRoles([]);
        setErrorIndexes([]);
        setDuplicateIndexes([]);
        setErrorsAcknowledged(false);
    };

    /**
     * Fetches only users who have a given role
     */
    const fetchUsersWithRole = useCallback(
        async (token: string) => {
            try {
                const result = await axios.get<UsersRecord[]>(
                    `${api}${USERS_DATA_PORTAL}?limit=1000&auth_mode=${authMode}&demo_content=${showDemoContent}`,
                    {
                        params: {
                            roles: 'admin,teacher,instructional-designer'
                        },
                        headers: getUserUuidHeaders(token as string)
                    }
                );

                setUsers(result?.data ?? []);
                setUsersFetchStatus('SUCCESS');
            } catch (err) {
                if (err instanceof AxiosError) {
                    setUsersFetchError(err);
                }

                setUsersFetchStatus('ERROR');
            }
        },
        [api, authMode, showDemoContent]
    );

    /**
     * Fetches a list of all users for an organization.
     */
    const fetchUsers = useCallback(
        async (token: string) => {
            try {
                const result = await axios.get<UsersRecord[]>(
                    `${api}${USERS_DATA_PORTAL}?auth_mode=${authMode}&demo_content=${showDemoContent}`,
                    {
                        params: {
                            user_uuid: token
                        }
                    }
                );

                setUsers(result?.data ?? []);
                setUsersFetchStatus('SUCCESS');
            } catch (err) {
                console.error(err);

                fetchUsersWithRole(token);
            }
        },
        [api, fetchUsersWithRole, authMode, showDemoContent]
    );

    /**
     * Adds a single user
     */
    const addUser = async (newUser: CreateUserRequestInput) => {
        try {
            const optimisticNewUser: UsersRecordExtended = {
                user_id: OPTIMISTIC_NEW_USER_ID,
                email: newUser.email,
                user_name: newUser.name,
                roles: newUser.roles,
                pendingChanges: true
            };

            // optimistic update
            setUsers((prevState) => [optimisticNewUser, ...prevState]);

            const { email, name, roles } = newUser;
            const postData: CreateUsersRequestInput = {
                user_uuid: uuid,
                users: [
                    {
                        email,
                        name,
                        roles
                    }
                ]
            };

            await axios.post(`${api}${USERS}`, postData);

            const successMessage = t('users.actions.add-user-success', { userName: newUser.name });
            enqueueSnackbar(successMessage, { variant: 'success' });

            fetchUsers(uuid);
        } catch (err) {
            const errorMessage = t('users.actions.add-user-error', { userName: newUser.name });
            enqueueSnackbar(errorMessage, { variant: 'error' });

            fetchUsers(uuid);
        }
    };

    /**
     * Updates a single user.
     */
    const updateUser = async (updates: UpdateUserInput) => {
        const oldUser = users.find((i) => i.user_id === updates.user_id);

        // optimistic update
        setUsers((prevState) =>
            prevState.map((user) => {
                if (user.user_id === updates.user_id) {
                    return {
                        ...updates,
                        pendingChanges: true
                    };
                }

                return user;
            })
        );

        try {
            const postData: UpdateUserRequestInput = {
                user_uuid: uuid,
                email: updates.email,
                name: updates.user_name,
                roles: updates.roles,
                user_id: updates.user_id,
                password: updates.password
            };

            await axios.put(`${api}${USERS}`, postData);

            const successMessage = t('users.actions.edit-user-success');
            enqueueSnackbar(successMessage, { variant: 'success' });

            setUsers((prevState) =>
                prevState.map((prevUser) => {
                    if (prevUser.user_id === updates.user_id) {
                        return {
                            ...updates,
                            pendingChanges: false
                        };
                    }

                    return prevUser;
                })
            );
        } catch (err) {
            let errorMessage: string;

            if ((err as AxiosError).response && (err as AxiosError).response?.status === 409) {
                errorMessage = t('users.actions.edit-user-error-email', { email: updates.email });
            } else {
                errorMessage = t('users.actions.edit-user-error');
            }

            enqueueSnackbar(errorMessage, { variant: 'error' });

            setUsers((prevState) =>
                prevState.map((user) => {
                    if (user.user_id === updates.user_id) {
                        return {
                            ...oldUser!,
                            pendingChanges: false
                        };
                    }

                    return user;
                })
            );
        }
    };

    /**
     * Adds users in bulk and creates/updates users
     * based on email matches.
     */
    const bulkUpsertUsers = async (newUsers: CreateUserRequestInput[]) => {
        let toBeAddedCount;
        let toBeUpdatedCount;

        setBulkUpsertUsersStatus('LOADING');

        // optimistic update
        setUsers((prevState) => {
            const { updatedRecords, updatedCount, addedCount } = upsertUsersOptimistic(prevState, newUsers);

            toBeAddedCount = addedCount;
            toBeUpdatedCount = updatedCount;

            return updatedRecords;
        });

        try {
            const payload: CreateUsersRequestInput = {
                user_uuid: uuid,
                users: newUsers
            };

            await axios.post(`${api}${USERS}`, payload);

            setBulkUpsertUsersStatus('SUCCESS');

            if (toBeAddedCount) {
                const successAddedMessage = t('admin-invite.admins-added-success', { adminCount: toBeAddedCount });
                enqueueSnackbar(successAddedMessage, { variant: 'success' });
            }

            if (toBeUpdatedCount) {
                const successUpdatedMessage = t('admin-invite.admins-updated-success', {
                    adminCount: toBeUpdatedCount
                });
                enqueueSnackbar(successUpdatedMessage, { variant: 'success' });
            }

            resetBulkUploadState();

            fetchUsers(uuid);
        } catch (err) {
            const genericMessage = t('admin-invite.generic-upload-error');
            enqueueSnackbar(genericMessage, { variant: 'error' });

            setBulkUpsertUsersStatus('ERROR');

            fetchUsers(uuid);
        }
    };

    /**
     * Deletes a user from an organization.
     */
    const deleteUser = async ({ user_id, user_name }: UsersRecord) => {
        // optimistic update
        let oldUser;

        setUsers((prevState) =>
            prevState.filter((user) => {
                if (user.user_id !== user_id) {
                    oldUser = user;
                    return true;
                }

                return false;
            })
        );

        const data = {
            user_ids: [user_id],
            user_uuid: uuid
        };

        try {
            await axios.delete(`${api}${USERS}`, { data });

            const successMessage = t('users.actions.delete-user-success', { userName: user_name });
            enqueueSnackbar(successMessage, { variant: 'success' });
        } catch (err) {
            const errorMessage = t('users.actions.delete-user-error');
            enqueueSnackbar(errorMessage, { variant: 'error' });

            setUsers((prevState) => [...prevState, oldUser]);
        }
    };

    useEffect(() => {
        if (usersFetchStatus === 'INITIAL' && uuid) {
            fetchUsers(uuid);
        }

        if (usersFetchStatus === 'LOADING') setUsersFetchStatus('SUCCESS');
    }, [api, fetchUsers, usersFetchStatus, uuid]);

    return {
        users,
        usersFetchStatus,
        usersFetchError,

        addUser,
        updateUser,
        deleteUser,

        selectedRoles,
        setSelectedRoles,

        usersForBulkUpload,
        setUsersForBulkUpload,

        errorIndexes,
        setErrorIndexes,
        duplicateIndexes,
        setDuplicateIndexes,
        errorsAcknowledged,
        setErrorsAcknowledged,
        maxUsersLimitExceeded,
        setMaxUsersLimitExceeded,

        fileNameForCSV,
        setFileNameForCSV,

        bulkUpsertUsers,
        bulkUpsertUsersStatus,

        resetBulkUploadState
    };
};

const AdminUsersContainer = createContainer(useAdminUsers);

export default AdminUsersContainer;
